import { NgComponentOutlet } from "@angular/common";
import {
    afterNextRender,
    Component,
    DOCUMENT,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    signal,
} from "@angular/core";

import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { Meta } from "@angular/platform-browser";

import { filter, switchMap, take } from "rxjs";
import { fromPromise } from "rxjs/internal/observable/innerFrom";

import { LayoutFacade, LayoutStateModule } from "@hermes/aphrodite-layout-state";
import { LoaderComponent, LoaderService } from "@hermes/aphrodite-loader";
import { SvgSymbolsComponent } from "@hermes/aphrodite-svg";
import {
    chinaPreconnectLinks,
    Context,
    LOCALE,
    preconnectLinks,
} from "@hermes/core-app-core";
import { ShellModule } from "@hermes/hermes-web-fragments-shell";
import { isChina, isKorea } from "@hermes/locale";
import { RiskifiedBeaconService } from "@hermes/core-riskified-beacon-service";
import { RouterComponentsModule } from "@hermes/core-router-components";
import { SensorsDataService } from "@hermes/core-sensors-data-service";
import { ShellFacade } from "@hermes/domains-shell-states-shell-state";
import { CustomerServiceContactFacade } from "@hermes/hermes-web-states-customer-service-contact-state";
import { ENABLE_LIVECHAT_V2, FeatureFlagFacade } from "@hermes/hermes-web-states-flipper-state";
import { TrayStateModule } from "@hermes/hermes-web-states-tray-state";
import { DOWN, LEFT, RIGHT, TAB, UP } from "@hermes/utils-generic-constants";
import { HeadService } from "@hermes/utils-generic-services-head";

/**
 * Note: all "initialization" calls should probably be refactored and migrated
 * to an "initializer" (cf provideAppInitializer() and similar)
 */
@Component({
    selector: "h-root",
    imports: [
        LayoutStateModule,
        LoaderComponent,
        NgComponentOutlet,
        RouterComponentsModule,
        ShellModule,
        SvgSymbolsComponent,
        TrayStateModule,
    ],
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    host: {
        "[attr.data-platform]": 'isBrowser ? "browser" : "server"',
    },
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly locale = inject(LOCALE);
    private readonly context = inject(Context);
    private readonly headService = inject(HeadService);
    private readonly loaderService = inject(LoaderService);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly featureFlagFacade = inject(FeatureFlagFacade);
    private readonly meta = inject(Meta);
    private readonly riskifiedBeaconService = inject(RiskifiedBeaconService);
    private readonly document = inject(DOCUMENT);
    private readonly sensorsDataService = inject(SensorsDataService);
    private readonly shellFacade = inject(ShellFacade);
    private readonly customerServiceContactFacade = inject(
        CustomerServiceContactFacade,
    );
    public readonly isBrowser = this.context.isInBrowserMode();

    // We do not precise the component class to not import the module
    protected readonly livechat = signal<(new () => unknown) | undefined>(
        undefined,
    );

    // Load livechat library through import
    // @defer is not lazy loading services, this stay the more optimized way
    private readonly livechatLibrary$ = this.featureFlagFacade
        .isActivated(ENABLE_LIVECHAT_V2)
        .pipe(
            takeUntilDestroyed(),
            // ENABLE_LIVECHAT_V2 is true
            filter(Boolean),
            // Load library
            switchMap(() => fromPromise(import("@hermes/hermes-web-fragments-livechat"))),
            // Only once
            take(1),
        );

    protected readonly isKeyboardNavigation = toSignal(
        this.layoutFacade.keyboardNavigation$,
        { initialValue: false },
    );

    private mutationObserver?: MutationObserver;

    constructor() {
        this.shellFacade.fetchBanners();
        this.customerServiceContactFacade.fetchCustomerServiceContacts();
        afterNextRender({
            write: () => this.addPrefetchLoaderImages(),
        });
    }

    @HostListener("window:mousedown")
    public onmousedown(): void {
        this.layoutFacade.enableMouseNavigation();
    }

    @HostListener("window:keydown", ["$event"])
    public onkeyDown(event: KeyboardEvent): void {
        if ([TAB, UP, DOWN, LEFT, RIGHT].includes(event.key)) {
            this.layoutFacade.enableKeyboardNavigation();
        }
    }

    public ngOnInit(): void {
        this.initHeadTag();
        if (this.context.isInBrowserMode()) {
            this.mutationObserver = new MutationObserver(() => {
                this.updateCookiePopinState();
            });

            this.mutationObserver.observe(this.document.body, {
                attributes: true,
                attributeFilter: ["class"],
            });
            this.updateCookiePopinState();

            // Load livechat component if needed
            // eslint-disable-next-line rxjs-angular-x/prefer-composition,rxjs-x/no-ignored-subscription
            this.livechatLibrary$.subscribe((livechat) => {
                this.livechat.set(livechat.LivechatComponent);
            });
        }

        this.riskifiedBeaconService.executeService();
        this.sensorsDataService.executeService();
    }

    public ngOnDestroy(): void {
        this.mutationObserver?.disconnect();
    }

    private updateCookiePopinState(): void {
        const bodyHasCookieClass =
            this.document.body.classList.contains("didomi-popup-open");
        this.layoutFacade.setCookiePopinVisible(bodyHasCookieClass);
    }

    private initHeadTag(): void {
        if (this.context.isInServerMode()) {
            if (isKorea(this.locale)) {
                this.meta.addTag({
                    name: "naver-site-verification",
                    content: "47f4da7577b025b5e37093d3feded3f25c5ebc7f",
                });
            }
            if (isChina(this.locale)) {
                this.addPreconnectLinks(chinaPreconnectLinks);
            } else {
                this.addPreconnectLinks(preconnectLinks);
                this.headService.loadDataDome();
            }
        }
    }

    private addPrefetchLoaderImages() {
        this.loaderService
            .getAllLoaderImageUrls()
            .forEach((href) => this.headService.addPrefetchImageLink({ href }));
    }

    private addPreconnectLinks(tags: string[]): void {
        tags.forEach((link) => {
            this.headService.addTagAfterTitle({
                rel: "preconnect",
                href: `//${link}`,
            });
        });
    }
}
